
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TableModule } from 'primeng/table';
import { SortEvent } from 'primeng/api';
import { Subject, catchError, debounceTime, finalize, forkJoin, of, takeUntil } from 'rxjs';
import { NgShuiModule } from '@shui/angular';
import { AuthService } from 'src/core/services/auth.service';
import { BusinessPartnerManagementService } from 'src/core/services/business-partner-management.service';
import { DataService } from 'src/core/services/data.service';
import {
  BusinessPartnerDraft,
  BusinessPartnerEditableSite,
  BusinessPartnerInstrument,
  BusinessPartnerRecord,
  BusinessPartnerSiteSummary,
} from 'src/core/types/business-partner.types';
import { ApiResponse } from 'src/core/types/general.types';
import { FunctionalLocationOverview } from 'src/core/types/funcloc.types';

type SortDirection = 1 | -1;
type InstrumentState = 'idle' | 'queued' | 'loading' | 'loaded' | 'error';
type PartnerOverviewRow = BusinessPartnerRecord & { countryCount: number; siteCount: number; manualUserCount: number };
type EditorStep = 1 | 2 | 3;
type BaseInfoField = 'companyName' | 'domainName';

@Component({
  selector: 'app-business-partner-management',
  standalone: true,
  imports: [FormsModule, TableModule, NgShuiModule, TranslocoModule],
  templateUrl: './business-partner-management.component.html',
  styleUrl: './business-partner-management.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BusinessPartnerManagementComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly siteTableRefresh$ = new Subject<void>();
  private readonly domainLabelPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
  private readonly emailAddressPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private instrumentDetailsLoadingVersion = 0;
  private instrumentDetailsPendingLoads = 0;
  private readonly publicMailboxDomains = new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.de', 'yahoo.fr',
    'hotmail.com', 'hotmail.de', 'hotmail.fr', 'hotmail.co.uk', 'outlook.com', 'live.com',
    'msn.com', 'aol.com', 'icloud.com', 'me.com', 'mac.com', 'protonmail.com', 'proton.me',
    'gmx.com', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'web.de', 't-online.de',
  ]);

  private get userBasic(): Record<string, string> {
    return (this.authService.userData?.basic ?? {}) as Record<string, string>;
  }

  @Output() closed = new EventEmitter<void>();

  viewMode: 'overview' | 'edit' = 'overview';
  loading = true;
  overviewError = false;
  siteCatalogLoading = false;
  siteCatalogError = false;
  serialFilterLoading = false;
  saving = false;
  validatingOnboarding = false;
  deleteModalOpen = false;
  deletingPartner = false;
  formError = '';

  overviewRows: PartnerOverviewRow[] = [];
  siteCatalog: BusinessPartnerEditableSite[] = [];
  allEditableSites: BusinessPartnerEditableSite[] = [];
  filteredSites: BusinessPartnerEditableSite[] = [];
  selectedSiteIds = new Set<string>();
  countriesDialogVisible = false;
  sitesDialogVisible = false;
  manualUsersDialogVisible = false;
  instrumentsDialogVisible = false;
  selectedPartnerDetails: PartnerOverviewRow | null = null;
  pendingDeletePartner: PartnerOverviewRow | null = null;
  selectedInstrumentSite: BusinessPartnerEditableSite | null = null;
  selectedInstrumentRows: BusinessPartnerInstrument[] = [];
  instrumentDetailsLoading = false;
  instrumentFilters = {
    serial: '',
    material: '',
    productFamily: '',
  };
  partnerSiteFilters = {
    siteName: '',
    address: '',
    city: '',
    country: '',
  };

  draft: BusinessPartnerDraft = this.createEmptyDraft();
  isCreating = false;
  editorStep: EditorStep = 1;
  baseInfoAttempted = false;
  manualUsersAttempted = false;
  originalDomainName = '';
  manualUserInputs: string[] = [''];
  private readonly blurredBaseInfoFields = new Set<BaseInfoField>();
  private readonly focusedBaseInfoFields = new Set<BaseInfoField>();
  private readonly blurredManualUserInputs = new Set<number>();
  private readonly focusedManualUserInputs = new Set<number>();

  siteFilters = {
    name: '',
    address: '',
    city: '',
    country: '',
    serial: '',
  };

  scopeValue: 'All Sites' | 'Selected Sites Only' = 'All Sites';
  readonly siteTableRows = 40;
  sortField = 'siteName';
  sortOrder: SortDirection = 1;

  instrumentState = new Map<string, InstrumentState>();
  instrumentCache = new Map<string, BusinessPartnerInstrument[]>();

  constructor(
    private authService: AuthService,
    private businessPartnerService: BusinessPartnerManagementService,
    private dataService: DataService,
    private translocoService: TranslocoService,
  ) { }

  ngOnInit(): void {
    this.siteTableRefresh$
      .pipe(
        debounceTime(0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applySiteFiltersAndSort();
      });

    this.loadOverview();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeDialog(): void {
    this.closed.emit();
  }

  closeDeleteModal(): void {
    if (this.deletingPartner) {
      return;
    }

    this.deleteModalOpen = false;
    this.pendingDeletePartner = null;
  }

  closeCountriesDialog(): void {
    this.countriesDialogVisible = false;
  }

  closeSitesDialog(): void {
    this.sitesDialogVisible = false;
    this.resetPartnerSiteFilterState();
  }

  closeManualUsersDialog(): void {
    this.manualUsersDialogVisible = false;
  }

  closeInstrumentsDialog(): void {
    this.instrumentsDialogVisible = false;
    this.selectedInstrumentSite = null;
    this.selectedInstrumentRows = [];
    this.resetInstrumentFilterState();
    this.resetInstrumentDetailsLoading();
  }

  loadOverview(): void {
    this.loading = true;
    this.overviewError = false;

    this.businessPartnerService.getBusinessPartners()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (partners) => {
          this.overviewRows = partners.map((partner) => ({
            ...partner,
            countryCount: partner.countries?.length ?? 0,
            siteCount: partner.sites?.length ?? 0,
            manualUserCount: partner.manuallyAddedUsers?.length ?? 0,
          }));
          this.loading = false;
        },
        error: () => {
          this.overviewError = true;
          this.loading = false;
        }
      });
  }

  startCreate(): void {
    this.isCreating = true;
    this.formError = '';
    this.draft = this.createEmptyDraft();
    this.selectedSiteIds = new Set();
    this.originalDomainName = '';
    this.viewMode = 'edit';
    this.editorStep = 1;
    this.baseInfoAttempted = false;
    this.manualUsersAttempted = false;
    this.blurredBaseInfoFields.clear();
    this.focusedBaseInfoFields.clear();
    this.blurredManualUserInputs.clear();
    this.focusedManualUserInputs.clear();
    this.setManualUserInputs([]);
    this.resetSiteFilterState();
  }

  editPartner(partner: PartnerOverviewRow): void {
    this.isCreating = false;
    this.formError = '';
    this.draft = {
      id: partner.id,
      companyName: partner.companyName,
      domainName: partner.domainName,
      access: this.createDefaultAccess(partner.access),
      sites: partner.sites.map((site) => ({ ...site })),
      manuallyAddedUsers: (partner.manuallyAddedUsers ?? []).map((email) => email),
    };
    this.selectedSiteIds = new Set(partner.sites.map((site) => site.uid));
    this.originalDomainName = partner.domainName;
    this.viewMode = 'edit';
    this.editorStep = 1;
    this.baseInfoAttempted = false;
    this.manualUsersAttempted = false;
    this.blurredBaseInfoFields.clear();
    this.focusedBaseInfoFields.clear();
    this.blurredManualUserInputs.clear();
    this.focusedManualUserInputs.clear();
    this.setManualUserInputs(partner.manuallyAddedUsers ?? []);
    this.resetSiteFilterState();
  }

  private prepareEditView(selectedSites: BusinessPartnerSiteSummary[]): void {
    this.viewMode = 'edit';
    this.resetSiteFilterState();

    if (this.siteCatalog.length > 0) {
      this.buildEditableSiteCatalog(selectedSites);
      return;
    }

    this.siteCatalogLoading = true;
    this.siteCatalogError = false;

    this.businessPartnerService.getBusinessPartnerSites()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sites) => {
          this.siteCatalog = sites;
          this.siteCatalogLoading = false;
          this.buildEditableSiteCatalog(selectedSites);
        },
        error: () => {
          this.siteCatalogLoading = false;
          this.siteCatalogError = true;
          this.buildEditableSiteCatalog(selectedSites);
        }
      });
  }

  private buildEditableSiteCatalog(selectedSites: BusinessPartnerSiteSummary[]): void {
    const selectedSiteMap = new Map(selectedSites.map((site) => [site.uid, site]));
    const catalog = [...this.siteCatalog];

    selectedSites.forEach((site) => {
      if (!catalog.some((catalogSite) => catalogSite.uid === site.uid)) {
        catalog.push({ ...site });
      }
    });

    this.allEditableSites = catalog.map((site) => {
      const selected = selectedSiteMap.get(site.uid);
      return {
        ...site,
        address: selected?.address ?? site.address,
        city: selected?.city ?? site.city,
        country: selected?.country ?? site.country,
        zip: selected?.zip ?? site.zip,
      };
    });

    this.requestSiteTableRefresh();
  }

  private resetSiteFilterState(): void {
    this.siteFilters = { name: '', address: '', city: '', country: '', serial: '' };
    this.scopeValue = 'All Sites';
    this.serialFilterLoading = false;
    this.sortField = 'siteName';
    this.sortOrder = 1;
  }

  returnToOverview(): void {
    this.viewMode = 'overview';
    this.editorStep = 1;
    this.baseInfoAttempted = false;
    this.manualUsersAttempted = false;
    this.blurredBaseInfoFields.clear();
    this.focusedBaseInfoFields.clear();
    this.blurredManualUserInputs.clear();
    this.focusedManualUserInputs.clear();
    this.originalDomainName = '';
    this.formError = '';
  }

  goToBaseInfoStep(): void {
    this.syncDraftSitesFromSelection();
    this.editorStep = 1;
    this.formError = '';
  }

  goToManualUsersStep(): void {
    this.syncDraftSitesFromSelection();
    this.syncDraftManualUsersFromInputs();
    this.editorStep = 2;
    this.formError = '';
  }

  canNavigateToUsersStep(): boolean {
    return this.isBaseInfoValid();
  }

  canNavigateToSiteStep(): boolean {
    return this.isBaseInfoValid() && this.isManualUsersStepValid();
  }

  navigateToEditorStep(step: EditorStep): void {
    if (step === 1) {
      if (this.editorStep !== 1) {
        this.goToBaseInfoStep();
      }
      return;
    }

    if (step === 2) {
      if (!this.canNavigateToUsersStep()) {
        this.baseInfoAttempted = true;
        this.formError = '';
        return;
      }

      if (this.editorStep !== 2) {
        this.continueToManualUsersStep();
      }
      return;
    }

    if (!this.canNavigateToUsersStep()) {
      this.baseInfoAttempted = true;
      this.formError = '';
      return;
    }

    if (!this.isManualUsersStepValid()) {
      this.manualUsersAttempted = true;
      this.formError = '';
      this.editorStep = 2;
      return;
    }

    if (this.editorStep !== 3) {
      this.continueToSiteStep();
    }
  }

  continueToManualUsersStep(): void {
    this.baseInfoAttempted = true;
    this.formError = '';

    if (!this.isBaseInfoValid()) {
      return;
    }

    this.draft.domainName = this.normaliseDomainName(this.draft.domainName);
    this.validateOnboardingConfiguration(this.isCreating ? [] : this.draft.manuallyAddedUsers, () => {
      this.editorStep = 2;
    });
  }

  continueToSiteStep(): void {
    this.baseInfoAttempted = true;
    this.manualUsersAttempted = true;
    this.formError = '';

    if (!this.isBaseInfoValid()) {
      this.editorStep = 1;
      return;
    }

    if (!this.isManualUsersStepValid()) {
      this.editorStep = 2;
      return;
    }

    this.draft.domainName = this.normaliseDomainName(this.draft.domainName);
    this.syncDraftManualUsersFromInputs();
    this.validateOnboardingConfiguration(this.draft.manuallyAddedUsers, () => {
      this.editorStep = 3;
      this.prepareEditView(this.getWorkingSelectedSites());
    });
  }

  onBaseInfoChanged(): void {
    this.formError = '';
  }

  onBaseInfoFocus(field: BaseInfoField): void {
    this.focusedBaseInfoFields.add(field);
  }

  onCompanyNameBlur(): void {
    this.focusedBaseInfoFields.delete('companyName');
    this.blurredBaseInfoFields.add('companyName');
  }

  onDomainNameChanged(value: string): void {
    this.draft.domainName = value.trim().toLowerCase();
    this.formError = '';
  }

  onDomainNameBlur(): void {
    this.focusedBaseInfoFields.delete('domainName');
    const normalised = this.normaliseDomainName(this.draft.domainName);
    if (normalised !== this.draft.domainName) {
      this.draft.domainName = normalised;
    }
    this.blurredBaseInfoFields.add('domainName');
  }

  getBaseInfoStepState(): 'success' | 'error' | undefined {
    if (this.editorStep > 1 && this.isBaseInfoValid()) {
      return 'success';
    }

    if (this.baseInfoAttempted && !this.isBaseInfoValid()) {
      return 'error';
    }

    return undefined;
  }

  getManualUsersStepState(): 'success' | 'error' | undefined {
    if (this.editorStep === 3 && this.isManualUsersStepValid()) {
      return 'success';
    }

    if (this.manualUsersAttempted && !this.isManualUsersStepValid()) {
      return 'error';
    }

    return undefined;
  }

  showCompanyNameError(): boolean {
    return this.shouldShowBaseInfoValidation('companyName') && Boolean(this.getCompanyNameValidationError());
  }

  showDomainNameError(): boolean {
    return this.shouldShowBaseInfoValidation('domainName') && Boolean(this.getDomainValidationError());
  }

  showCompanyNameSuccess(): boolean {
    return this.shouldShowBaseInfoValidation('companyName')
      && !this.getCompanyNameValidationError()
      && this.isCompanyNameValid();
  }

  showDomainNameSuccess(): boolean {
    return this.shouldShowBaseInfoValidation('domainName')
      && !this.getDomainValidationError()
      && this.isDomainNameValid();
  }

  showCapabilitiesError(): boolean {
    return this.baseInfoAttempted && Boolean(this.getCapabilitiesValidationError());
  }

  getCompanyNameValidationError(): string {
    const companyName = this.draft.companyName.trim();

    if (!companyName) {
      return this.translocoService.translate('businessPartner.validation.companyNameRequired');
    }

    if (companyName.length < 2) {
      return this.translocoService.translate('businessPartner.validation.companyNameMinLength');
    }

    return '';
  }

  getDomainValidationError(): string {
    const raw = this.draft.domainName.trim();

    if (!raw) {
      return this.translocoService.translate('businessPartner.validation.domainRequired');
    }

    const domain = this.normaliseDomainName(raw);

    // Reject bare IP addresses
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(domain)) {
      return this.translocoService.translate('businessPartner.validation.domainNotIp');
    }

    // Must contain at least one dot
    if (!domain.includes('.')) {
      return this.translocoService.translate('businessPartner.validation.domainNeedExtension');
    }

    const labels = domain.split('.');
    const tld = labels[labels.length - 1];

    // TLD must be letters only and at least 2 chars
    if (!/^[a-z]{2,}$/.test(tld)) {
      return this.translocoService.translate('businessPartner.validation.domainInvalidTld', { tld });
    }

    // Validate each label
    for (const label of labels) {
      if (label.length === 0) {
        return this.translocoService.translate('businessPartner.validation.domainEmptySegment');
      }
      if (label.length > 63) {
        return this.translocoService.translate('businessPartner.validation.domainSegmentTooLong', { segment: label.slice(0, 20) });
      }
      if (!this.domainLabelPattern.test(label)) {
        return this.translocoService.translate('businessPartner.validation.domainInvalidChars');
      }
    }

    // Total length cap (RFC 1035)
    if (domain.length > 253) {
      return this.translocoService.translate('businessPartner.validation.domainTooLong');
    }

    // Block well-known public / consumer mailbox providers
    if (this.publicMailboxDomains.has(domain)) {
      return this.translocoService.translate('businessPartner.validation.domainPublicMailbox');
    }

    // Duplicate check (ignore the record currently being edited)
    const duplicateExists = this.overviewRows.some(
      (p) => p.domainName === domain && p.domainName !== this.originalDomainName,
    );

    if (duplicateExists) {
      return this.translocoService.translate('businessPartner.validation.domainDuplicate');
    }

    return '';
  }

  getCapabilitiesValidationError(): string {
    if (!this.hasSelectedCapability()) {
      return this.translocoService.translate('businessPartner.validation.capabilityRequired');
    }

    return '';
  }

  isCompanyNameValid(): boolean {
    return this.draft.companyName.trim().length >= 2;
  }

  isDomainNameValid(): boolean {
    const domainName = this.normaliseDomainName(this.draft.domainName);
    return Boolean(domainName) && !this.getDomainValidationError();
  }

  hasSelectedCapability(): boolean {
    return this.draft.access.chatbot || this.draft.access.machineData || this.draft.access.serviceData;
  }

  onManualUserInputChange(index: number, value: string): void {
    this.manualUserInputs[index] = `${value ?? ''}`.trim();
    this.formError = '';
  }

  onManualUserInputFocus(index: number): void {
    this.focusedManualUserInputs.add(index);
  }

  onManualUserInputBlur(index: number): void {
    this.focusedManualUserInputs.delete(index);
    this.manualUserInputs[index] = this.normaliseEmailAddress(this.manualUserInputs[index]);
    this.blurredManualUserInputs.add(index);
  }

  trackManualUserInput(index: number): number {
    return index;
  }

  addManualUserInput(): void {
    const lastIndex = this.manualUserInputs.length - 1;

    if (!this.canAddManualUserInput(lastIndex)) {
      return;
    }

    this.manualUserInputs = [...this.manualUserInputs, ''];
    this.formError = '';
  }

  isAddManualUserButtonVisible(index: number): boolean {
    return index === this.manualUserInputs.length - 1;
  }

  canAddManualUserInput(index: number): boolean {
    return this.isAddManualUserButtonVisible(index)
      && Boolean(this.normaliseEmailAddress(this.manualUserInputs[index]))
      && this.isManualUserValidationVisible(index)
      && !this.getManualUserValidationError(index);
  }

  showManualUserError(index: number): boolean {
    const email = this.normaliseEmailAddress(this.manualUserInputs[index]);
    return this.isManualUserValidationVisible(index) && Boolean(email) && Boolean(this.getManualUserValidationError(index));
  }

  showManualUserSuccess(index: number): boolean {
    const email = this.normaliseEmailAddress(this.manualUserInputs[index]);
    return this.isManualUserValidationVisible(index) && Boolean(email) && !this.getManualUserValidationError(index);
  }

  getManualUserValidationError(index: number): string {
    const email = this.normaliseEmailAddress(this.manualUserInputs[index]);

    if (!email) {
      return '';
    }

    if (!this.emailAddressPattern.test(email)) {
      return this.translocoService.translate('businessPartner.validation.emailInvalid');
    }

    if (this.getDuplicateManualUserEmails().has(email)) {
      return this.translocoService.translate('businessPartner.validation.emailDuplicate');
    }

    if (this.getExistingManualUserEmails().has(email)) {
      return this.translocoService.translate('businessPartner.validation.emailAssigned');
    }

    return '';
  }

  getConfiguredManualUserCount(): number {
    return this.getNormalisedManualUserEmails().length;
  }

  private isBaseInfoValid(): boolean {
    return !this.getCompanyNameValidationError()
      && !this.getDomainValidationError()
      && !this.getCapabilitiesValidationError();
  }

  private isManualUsersStepValid(): boolean {
    return this.manualUserInputs.every((_, index) => !this.getManualUserValidationError(index));
  }

  private shouldShowBaseInfoValidation(field: BaseInfoField): boolean {
    return !this.focusedBaseInfoFields.has(field)
      && (this.baseInfoAttempted || this.blurredBaseInfoFields.has(field));
  }

  private isManualUserValidationVisible(index: number): boolean {
    return !this.focusedManualUserInputs.has(index)
      && (this.manualUsersAttempted || this.blurredManualUserInputs.has(index));
  }

  private normaliseDomainName(value: string): string {
    let v = value.trim().toLowerCase();

    // Strip protocol prefixes
    v = v.replace(/^https?:\/\//i, '');

    // Strip path, query string, fragment, and port
    v = v.split('/')[0].split('?')[0].split('#')[0].split(':')[0];

    // Extract domain from an email address (take the part after @)
    if (v.includes('@')) {
      v = v.split('@').slice(-1)[0] ?? v;
    }

    return v.trim();
  }

  private normaliseEmailAddress(value: string): string {
    return `${value ?? ''}`.trim().toLowerCase();
  }

  private getNormalisedManualUserEmails(): string[] {
    return this.manualUserInputs
      .map((email) => this.normaliseEmailAddress(email))
      .filter(Boolean);
  }

  private getDuplicateManualUserEmails(): Set<string> {
    const counts = new Map<string, number>();

    this.getNormalisedManualUserEmails().forEach((email) => {
      counts.set(email, (counts.get(email) ?? 0) + 1);
    });

    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([email]) => email)
    );
  }

  private getExistingManualUserEmails(): Set<string> {
    return new Set(
      this.overviewRows
        .filter((partner) => partner.domainName !== this.originalDomainName)
        .flatMap((partner) => partner.manuallyAddedUsers ?? [])
        .map((email) => this.normaliseEmailAddress(email))
        .filter(Boolean)
    );
  }

  private setManualUserInputs(emails: string[]): void {
    const normalisedEmails = (emails ?? []).map((email) => this.normaliseEmailAddress(email)).filter(Boolean);
    this.manualUserInputs = [...normalisedEmails, ''];
    this.blurredManualUserInputs.clear();
    this.focusedManualUserInputs.clear();
  }

  onScopeChange(scope: 'All Sites' | 'Selected Sites Only'): void {
    this.scopeValue = scope;
    this.requestSiteTableRefresh();
  }

  onSiteFilterChange(field: keyof typeof this.siteFilters, valueOrEvent: string | Event): void {
    this.siteFilters[field] = this.getSearchValue(valueOrEvent);
    this.requestSiteTableRefresh();
  }

  onSerialFilterChange(valueOrEvent: string | Event): void {
    const value = this.getSearchValue(valueOrEvent);
    this.siteFilters.serial = value;
    this.requestSiteTableRefresh();
  }

  private requestSiteTableRefresh(): void {
    this.siteTableRefresh$.next();
  }

  private applySiteFiltersAndSort(): void {
    const name = this.siteFilters.name.trim().toLowerCase();
    const address = this.siteFilters.address.trim().toLowerCase();
    const city = this.siteFilters.city.trim().toLowerCase();
    const country = this.siteFilters.country.trim().toLowerCase();
    const serial = this.siteFilters.serial.trim();

    let sites = [...this.allEditableSites];

    if (this.scopeValue === 'Selected Sites Only') {
      sites = sites.filter((site) => this.selectedSiteIds.has(site.uid));
    }

    if (name) {
      sites = sites.filter((site) => site.siteName.toLowerCase().includes(name));
    }

    if (address) {
      sites = sites.filter((site) => site.address.toLowerCase().includes(address));
    }

    if (city) {
      sites = sites.filter((site) => site.city.toLowerCase().includes(city));
    }

    if (country) {
      sites = sites.filter((site) => site.country.toLowerCase().includes(country));
    }

    if (serial && serial.length >= 3) {
      const lowerSerial = serial.toLowerCase();

      sites = sites.filter((site) => {
        const serials = site.instrumentSerials ?? this.instrumentCache.get(site.uid)?.map((instrument) => instrument.serialNumber) ?? [];
        return serials.some((instrumentSerial) => instrumentSerial?.toLowerCase().includes(lowerSerial));
      });
    } else {
      this.serialFilterLoading = false;
    }

    sites.sort((left, right) => this.compareSites(left, right, this.sortField, this.sortOrder));

    this.filteredSites = sites;
  }

  private compareSites(left: BusinessPartnerEditableSite, right: BusinessPartnerEditableSite, field: string, order: SortDirection): number {
    let leftValue: string | number | boolean = '';
    let rightValue: string | number | boolean = '';

    switch (field) {
      case 'included':
        leftValue = this.selectedSiteIds.has(left.uid);
        rightValue = this.selectedSiteIds.has(right.uid);
        break;
      case 'instrumentSummary':
        leftValue = this.getInstrumentCount(left);
        rightValue = this.getInstrumentCount(right);
        break;
      default:
        leftValue = (left as unknown as Record<string, string | number | boolean>)[field] ?? '';
        rightValue = (right as unknown as Record<string, string | number | boolean>)[field] ?? '';
        break;
    }

    const compared = this.compareValues(leftValue, rightValue);
    return compared * order;
  }

  private compareValues(left: string | number | boolean, right: string | number | boolean): number {
    if (typeof left === 'boolean' && typeof right === 'boolean') {
      return Number(left) - Number(right);
    }

    if (typeof left === 'number' && typeof right === 'number') {
      return left - right;
    }

    return `${left}`.localeCompare(`${right}`, undefined, { numeric: true, sensitivity: 'base' });
  }

  onSiteSort(event: SortEvent): void {
    this.sortField = `${event.field ?? 'siteName'}`;
    this.sortOrder = (event.order === -1 ? -1 : 1);
    this.requestSiteTableRefresh();
  }

  private getSearchValue(valueOrEvent: string | Event): string {
    if (typeof valueOrEvent === 'string') {
      return valueOrEvent;
    }

    const target = valueOrEvent.target as { value?: unknown } | null;
    return typeof target?.value === 'string' ? target.value : `${target?.value ?? ''}`;
  }

  private queueInstrumentLoad(site: BusinessPartnerEditableSite): void {
    const currentState = this.instrumentState.get(site.uid);

    if (currentState === 'loading' || currentState === 'loaded') {
      return;
    }

    this.fetchInstrumentDetails(site);
  }

  private fetchInstrumentDetails(site: BusinessPartnerEditableSite): void {
    const loadingVersion = this.selectedInstrumentSite?.uid === site.uid
      ? this.beginInstrumentDetailsLoading()
      : null;

    if (!site.siteId) {
      this.instrumentState.set(site.uid, 'error');
      if (loadingVersion !== null) {
        this.finishInstrumentDetailsLoading(loadingVersion);
      }
      return;
    }

    this.instrumentState.set(site.uid, 'loading');

    this.dataService.getFunctionalLocationOverview(site.siteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ response }: ApiResponse<FunctionalLocationOverview>) => {
          const instruments = (response?.device_data ?? []).map((device) => ({
            serialNumber: device.serial_number,
            materialNumber: device.material_number,
            productFamily: device.product_family,
            moduleType: device.module_type,
            totallyDown: device.totally_down,
          }));

          this.instrumentCache.set(site.uid, instruments);
          this.instrumentState.set(site.uid, 'loaded');

          if (this.selectedInstrumentSite?.uid === site.uid) {
            this.selectedInstrumentRows = instruments;
            this.prefillInstrumentRows(site.uid, instruments);
          }

          if (loadingVersion !== null) {
            this.finishInstrumentDetailsLoading(loadingVersion);
          }
        },
        error: () => {
          this.instrumentState.set(site.uid, 'error');

          if (this.siteFilters.serial.trim().length >= 3) {
            this.requestSiteTableRefresh();
          }

          if (loadingVersion !== null) {
            this.finishInstrumentDetailsLoading(loadingVersion);
          }
        }
      });
  }

  toggleSite(site: BusinessPartnerEditableSite): void {
    if (this.selectedSiteIds.has(site.uid)) {
      this.selectedSiteIds.delete(site.uid);
    } else {
      this.selectedSiteIds.add(site.uid);
    }

    this.requestSiteTableRefresh();
  }

  toggleAllFiltered(): void {
    const everySelected = this.areAllFilteredSelected();

    this.filteredSites.forEach((site) => {
      if (everySelected) {
        this.selectedSiteIds.delete(site.uid);
      } else {
        this.selectedSiteIds.add(site.uid);
      }
    });

    this.requestSiteTableRefresh();
  }

  areAllFilteredSelected(): boolean {
    return this.filteredSites.length > 0 && this.filteredSites.every((site) => this.selectedSiteIds.has(site.uid));
  }

  hasActiveFilters(): boolean {
    return Boolean(
      this.siteFilters.name
      || this.siteFilters.address
      || this.siteFilters.city
      || this.siteFilters.country
      || this.siteFilters.serial
      || this.scopeValue === 'Selected Sites Only'
    );
  }

  getSelectedSiteCount(): number {
    return this.selectedSiteIds.size;
  }

  getInstrumentState(site: BusinessPartnerEditableSite): InstrumentState {
    return this.instrumentState.get(site.uid) ?? 'idle';
  }

  getInstrumentSummary(site: BusinessPartnerEditableSite): string {
    const instrumentSerials = site.instrumentSerials ?? [];
    if (instrumentSerials.length > 0) {
      return instrumentSerials.join(', ');
    }

    const instruments = this.instrumentCache.get(site.uid);
    if (instruments && instruments.length > 0) {
      return instruments
        .map((instrument) => instrument.serialNumber)
        .filter(Boolean)
        .join(', ');
    }

    const state = this.getInstrumentState(site);
    if (state === 'error') {
      return this.translocoService.translate('businessPartner.sites.instrumentsUnableToLoad');
    }

    if (state === 'loaded') {
      return this.translocoService.translate('businessPartner.sites.instrumentsNone');
    }

    return this.translocoService.translate('businessPartner.sites.instrumentsLoading');
  }

  getInstrumentCount(site: BusinessPartnerEditableSite): number {
    return site.instrumentSerials?.length ?? this.instrumentCache.get(site.uid)?.length ?? 0;
  }

  openInstrumentDetails(site: BusinessPartnerEditableSite): void {
    const cachedInstruments = this.instrumentCache.get(site.uid);
    const catalogInstruments = this.createCatalogInstrumentRows(site);
    const instruments = cachedInstruments ?? catalogInstruments;

    if (!cachedInstruments && catalogInstruments.length > 0) {
      this.instrumentCache.set(site.uid, catalogInstruments);
      this.instrumentState.set(site.uid, 'loaded');
    }

    this.selectedInstrumentSite = site;
    this.selectedInstrumentRows = instruments;
    this.resetInstrumentFilterState();
    this.instrumentsDialogVisible = true;

    if (!cachedInstruments && catalogInstruments.length === 0) {
      this.queueInstrumentLoad(site);
      return;
    }

    this.prefillInstrumentRows(site.uid, instruments);
  }

  onInstrumentFilterChange(field: keyof typeof this.instrumentFilters, valueOrEvent: string | Event): void {
    this.instrumentFilters[field] = this.getSearchValue(valueOrEvent);
  }

  getFilteredInstrumentRows(): BusinessPartnerInstrument[] {
    const serial = this.instrumentFilters.serial.trim().toLowerCase();
    const material = this.instrumentFilters.material.trim().toLowerCase();
    const productFamily = this.instrumentFilters.productFamily.trim().toLowerCase();

    return this.selectedInstrumentRows.filter((instrument) => {
      if (serial && !(instrument.serialNumber ?? '').toLowerCase().includes(serial)) {
        return false;
      }

      if (material && !(instrument.materialNumber ?? '').toLowerCase().includes(material)) {
        return false;
      }

      if (productFamily && !(instrument.productFamily ?? '').toLowerCase().includes(productFamily)) {
        return false;
      }

      return true;
    });
  }

  openCountriesDialog(partner: PartnerOverviewRow): void {
    this.selectedPartnerDetails = partner;
    this.countriesDialogVisible = true;
  }

  openSitesDialog(partner: PartnerOverviewRow): void {
    this.selectedPartnerDetails = partner;
    this.resetPartnerSiteFilterState();
    this.sitesDialogVisible = true;
  }

  openManualUsersDialog(partner: PartnerOverviewRow): void {
    this.selectedPartnerDetails = partner;
    this.manualUsersDialogVisible = true;
  }

  onPartnerSiteFilterChange(field: keyof typeof this.partnerSiteFilters, valueOrEvent: string | Event): void {
    this.partnerSiteFilters[field] = this.getSearchValue(valueOrEvent);
  }

  toggleAccess(field: keyof BusinessPartnerDraft['access']): void {
    if (field === 'chatbot') {
      return;
    }

    this.draft.access[field] = !this.draft.access[field];
    this.formError = '';
  }

  saveDraft(): void {
    this.baseInfoAttempted = true;
    this.manualUsersAttempted = true;

    if (!this.isBaseInfoValid()) {
      this.editorStep = 1;
      return;
    }

    if (!this.isManualUsersStepValid()) {
      this.editorStep = 2;
      return;
    }

    const companyName = this.draft.companyName.trim();
    const domainName = this.normaliseDomainName(this.draft.domainName);

    this.draft.domainName = domainName;
    this.syncDraftManualUsersFromInputs();

    const selectedSites = this.getWorkingSelectedSites();
    this.draft.sites = selectedSites;

    this.saving = true;
    this.formError = '';

    this.businessPartnerService.upsertBusinessPartner({
      id: domainName,
      companyName,
      domainName,
      access: this.createDefaultAccess(this.draft.access),
      sites: selectedSites,
      manuallyAddedUsers: this.draft.manuallyAddedUsers,
      previousDomainName: this.isCreating ? undefined : this.originalDomainName,
      updatedBy: `${this.userBasic['givenName'] ?? ''} ${this.userBasic['surname'] ?? ''}`.trim(),
      updatedByMail: this.userBasic['mail'] ?? '',
    })
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.returnToOverview();
          this.loadOverview();
        },
        error: (error) => {
          this.applyOnboardingValidationError(error, this.translocoService.translate('businessPartner.editor.saveError'));
        }
      });
  }

  deletePartner(partner: PartnerOverviewRow): void {
    this.pendingDeletePartner = partner;
    this.deleteModalOpen = true;
  }

  confirmDeletePartner(): void {
    if (!this.pendingDeletePartner) {
      return;
    }

    this.deletingPartner = true;

    this.businessPartnerService.deleteBusinessPartner(this.pendingDeletePartner.domainName)
      .pipe(
        finalize(() => {
          this.deletingPartner = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.deleteModalOpen = false;
          this.pendingDeletePartner = null;
          this.loadOverview();
        },
        error: () => {
          this.deleteModalOpen = false;
          this.pendingDeletePartner = null;
          this.overviewError = true;
        }
      });
  }

  formatDate(value: string | undefined): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(parsed);
  }

  getSelectedPartnerCountries(): string[] {
    return this.selectedPartnerDetails?.countries ?? [];
  }

  getSelectedPartnerSites(): BusinessPartnerSiteSummary[] {
    return this.selectedPartnerDetails?.sites ?? [];
  }

  getSelectedPartnerManualUsers(): string[] {
    return this.selectedPartnerDetails?.manuallyAddedUsers ?? [];
  }

  getFilteredSelectedPartnerSites(): BusinessPartnerSiteSummary[] {
    const siteName = this.partnerSiteFilters.siteName.trim().toLowerCase();
    const address = this.partnerSiteFilters.address.trim().toLowerCase();
    const city = this.partnerSiteFilters.city.trim().toLowerCase();
    const country = this.partnerSiteFilters.country.trim().toLowerCase();

    return this.getSelectedPartnerSites().filter((site) => {
      if (siteName && !(site.siteName ?? '').toLowerCase().includes(siteName)) {
        return false;
      }

      if (address && !(site.address ?? '').toLowerCase().includes(address)) {
        return false;
      }

      if (city && !(site.city ?? '').toLowerCase().includes(city)) {
        return false;
      }

      if (country && !(site.country ?? '').toLowerCase().includes(country)) {
        return false;
      }

      return true;
    });
  }

  private createCatalogInstrumentRows(site: BusinessPartnerEditableSite): BusinessPartnerInstrument[] {
    return (site.instruments ?? []).map((instrument) => ({
      serialNumber: instrument.serialNumber,
      materialNumber: instrument.materialNumber,
    }));
  }

  private prefillInstrumentRows(siteUid: string, instruments: BusinessPartnerInstrument[]): void {
    const loadableInstruments = instruments.filter((instrument) => Boolean(instrument.serialNumber && instrument.materialNumber));

    if (loadableInstruments.length === 0) {
      return;
    }

    const loadingVersion = this.beginInstrumentDetailsLoading();

    forkJoin(
      loadableInstruments.map((instrument) =>
        this.dataService.searchExact(instrument.materialNumber, instrument.serialNumber).pipe(
          catchError(() => of(null))
        )
      )
    )
      .pipe(
        finalize(() => {
          this.finishInstrumentDetailsLoading(loadingVersion);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        results.forEach((exact, index) => {
          const instrument = loadableInstruments[index];

          if (!exact?.response) {
            return;
          }

          const updatedInstrument: BusinessPartnerInstrument = {
            ...instrument,
            serialNumber: exact.response.SERIAL_NUMBER || instrument.serialNumber,
            materialNumber: exact.response.MATERIAL_NUMBER || instrument.materialNumber,
            productFamily: exact.response.ProductFamily || instrument.productFamily,
            moduleType: instrument.moduleType,
          };

          this.replaceInstrumentRow(siteUid, instrument, updatedInstrument);
        });
      });
  }

  private beginInstrumentDetailsLoading(): number {
    const loadingVersion = this.instrumentDetailsLoadingVersion;
    this.instrumentDetailsPendingLoads += 1;
    this.instrumentDetailsLoading = true;
    return loadingVersion;
  }

  private finishInstrumentDetailsLoading(loadingVersion: number): void {
    if (loadingVersion !== this.instrumentDetailsLoadingVersion) {
      return;
    }

    this.instrumentDetailsPendingLoads = Math.max(0, this.instrumentDetailsPendingLoads - 1);
    this.instrumentDetailsLoading = this.instrumentDetailsPendingLoads > 0;
  }

  private resetInstrumentDetailsLoading(): void {
    this.instrumentDetailsLoadingVersion += 1;
    this.instrumentDetailsPendingLoads = 0;
    this.instrumentDetailsLoading = false;
  }

  private resetInstrumentFilterState(): void {
    this.instrumentFilters = {
      serial: '',
      material: '',
      productFamily: '',
    };
  }

  private resetPartnerSiteFilterState(): void {
    this.partnerSiteFilters = {
      siteName: '',
      address: '',
      city: '',
      country: '',
    };
  }

  private replaceInstrumentRow(
    siteUid: string,
    currentInstrument: BusinessPartnerInstrument,
    updatedInstrument: BusinessPartnerInstrument,
  ): void {
    const updateRows = (rows: BusinessPartnerInstrument[]): BusinessPartnerInstrument[] => {
      const index = rows.findIndex((row) => this.isMatchingInstrumentRow(row, currentInstrument));

      if (index === -1) {
        return rows;
      }

      const nextRows = [...rows];
      nextRows[index] = {
        ...rows[index],
        ...updatedInstrument,
      };
      return nextRows;
    };

    const cachedRows = this.instrumentCache.get(siteUid);
    if (cachedRows) {
      this.instrumentCache.set(siteUid, updateRows(cachedRows));
    }

    if (this.selectedInstrumentSite?.uid === siteUid) {
      this.selectedInstrumentRows = updateRows(this.selectedInstrumentRows);
    }
  }

  private isMatchingInstrumentRow(candidate: BusinessPartnerInstrument, reference: BusinessPartnerInstrument): boolean {
    return candidate.serialNumber === reference.serialNumber
      && (!reference.materialNumber || !candidate.materialNumber || candidate.materialNumber === reference.materialNumber);
  }

  private createDefaultAccess(access?: BusinessPartnerDraft['access']): BusinessPartnerDraft['access'] {
    return {
      chatbot: true,
      machineData: access?.machineData ?? false,
      serviceData: access?.serviceData ?? false,
    };
  }

  private createEmptyDraft(): BusinessPartnerDraft {
    return {
      companyName: '',
      domainName: '',
      access: this.createDefaultAccess(),
      sites: [],
      manuallyAddedUsers: [],
    };
  }

  private syncDraftSitesFromSelection(): void {
    this.draft.sites = this.getWorkingSelectedSites();
  }

  private syncDraftManualUsersFromInputs(): void {
    const uniqueManualUsers = Array.from(new Set(this.getNormalisedManualUserEmails()));
    this.draft.manuallyAddedUsers = uniqueManualUsers;
    this.setManualUserInputs(uniqueManualUsers);
  }

  private validateOnboardingConfiguration(manuallyAddedUsers: string[], onValid: () => void): void {
    if (this.validatingOnboarding) {
      return;
    }

    this.validatingOnboarding = true;
    this.formError = '';

    this.businessPartnerService.validateBusinessPartnerOnboarding({
      domainName: this.normaliseDomainName(this.draft.domainName),
      manuallyAddedUsers,
      previousDomainName: this.isCreating ? undefined : this.originalDomainName,
    })
      .pipe(
        finalize(() => {
          this.validatingOnboarding = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          onValid();
        },
        error: (error) => {
          this.applyOnboardingValidationError(error, this.translocoService.translate('businessPartner.editor.validateError'));
        }
      });
  }

  private applyOnboardingValidationError(error: any, fallbackMessage: string): void {
    const field = `${error?.error?.field ?? ''}`;

    if (field === 'domainName') {
      this.baseInfoAttempted = true;
      this.editorStep = 1;
    } else if (field === 'manuallyAddedUsers') {
      this.manualUsersAttempted = true;
      this.editorStep = 2;
    }

    this.formError = error?.error?.error || fallbackMessage;
  }

  private getWorkingSelectedSites(): BusinessPartnerSiteSummary[] {
    if (this.allEditableSites.length === 0) {
      return this.draft.sites.map((site) => ({ ...site }));
    }

    return this.allEditableSites
      .filter((site) => this.selectedSiteIds.has(site.uid))
      .map((site) => ({
        uid: site.uid,
        siteId: site.siteId,
        siteName: site.siteName,
        address: site.address,
        city: site.city,
        country: site.country,
        zip: site.zip,
        support: site.support,
      }));
  }
}
